/**
 * Modern Cart Service
 *
 * Lambda-based cart service using DynamoDB via API Gateway
 * Replaces legacy Supabase cart implementation
 */

import { lambdaCartApiService, type LambdaCartSession, type LambdaCartItem } from './LambdaCartApiService';
import { logger } from '@/utils/logger';

export interface ModernCartSession {
  id: string;
  user_id: string;
  created_at: string;
  expires_at?: string;
  metadata?: Record<string, any>;
}

export interface ModernCartItem {
  id: string;
  session_id: string;
  source_type: 'quote' | 'product' | 'consolidation';
  source_id: string;
  quantity: number;
  original_amount: number;
  currency: string;
  discounted_amount?: number;
  addon_cost?: number;
  selected_addon_services?: any[];
  applied_discounts?: any[];
  selected_options?: Record<string, any>;
  catalog_variant_id?: string;
  created_at: string;
  updated_at: string;
}

export class ModernCartService {
  private static instance: ModernCartService;

  private constructor() {}

  static getInstance(): ModernCartService {
    if (!ModernCartService.instance) {
      ModernCartService.instance = new ModernCartService();
    }
    return ModernCartService.instance;
  }

  /**
   * Get cart sessions for user via Lambda API
   */
  async getCartSessions(userId: string): Promise<ModernCartSession[]> {
    try {
      const lambdaSessions = await lambdaCartApiService.getCartSessions(userId);
      logger.info('[ModernCartService] Retrieved cart sessions via Lambda API', { userId, count: lambdaSessions.length });

      return lambdaSessions.map(session => ({
        id: session.id,
        user_id: session.user_id,
        created_at: session.created_at,
        expires_at: session.expires_at,
        metadata: session.metadata
      }));
    } catch (error) {
      logger.error('[ModernCartService] Failed to get cart sessions', { userId, error: error.message });
      throw error;
    }
  }

  /**
   * Get single cart session via Lambda API
   */
  async getCartSession(sessionId: string): Promise<ModernCartSession | null> {
    try {
      const lambdaSession = await lambdaCartApiService.getCartSession(sessionId);
      if (!lambdaSession) {
        return null;
      }

      logger.info('[ModernCartService] Retrieved cart session via Lambda API', { sessionId });
      return {
        id: lambdaSession.id,
        user_id: lambdaSession.user_id,
        created_at: lambdaSession.created_at,
        expires_at: lambdaSession.expires_at,
        metadata: lambdaSession.metadata
      };
    } catch (error) {
      logger.error('[ModernCartService] Failed to get cart session', { sessionId, error: error.message });
      return null;
    }
  }

  /**
   * Create cart session via Lambda API
   */
  async createCartSession(userId: string): Promise<ModernCartSession> {
    try {
      const lambdaSession = await lambdaCartApiService.createCartSession(userId);
      logger.info('[ModernCartService] Created cart session via Lambda API', { sessionId: lambdaSession.id, userId });

      return {
        id: lambdaSession.id,
        user_id: lambdaSession.user_id,
        created_at: lambdaSession.created_at,
        expires_at: lambdaSession.expires_at,
        metadata: lambdaSession.metadata
      };
    } catch (error) {
      logger.error('[ModernCartService] Failed to create cart session', { userId, error: error.message });
      throw error;
    }
  }

  /**
   * Get cart items for session via Lambda API
   */
  async getCartItems(sessionId: string): Promise<ModernCartItem[]> {
    try {
      const lambdaItems = await lambdaCartApiService.getCartItems(sessionId);
      logger.info('[ModernCartService] Retrieved cart items via Lambda API', { sessionId, count: lambdaItems.length });

      return lambdaItems.map(item => ({
        id: item.id,
        session_id: item.session_id,
        source_type: item.source_type,
        source_id: item.source_id,
        quantity: item.quantity,
        original_amount: item.original_amount,
        currency: item.currency,
        discounted_amount: item.discounted_amount,
        addon_cost: item.addon_cost,
        selected_addon_services: item.selected_addon_services,
        applied_discounts: item.applied_discounts,
        selected_options: item.selected_options,
        catalog_variant_id: item.catalog_variant_id,
        created_at: item.created_at,
        updated_at: item.updated_at
      }));
    } catch (error) {
      logger.error('[ModernCartService] Failed to get cart items', { sessionId, error: error.message });
      throw error;
    }
  }

  /**
   * Add item to cart via Lambda API
   */
  async addCartItem(sessionId: string, item: Omit<ModernCartItem, 'id' | 'session_id' | 'created_at' | 'updated_at'>): Promise<ModernCartItem> {
    try {
      const lambdaItem = await lambdaCartApiService.addCartItem(sessionId, item);
      logger.info('[ModernCartService] Added cart item via Lambda API', { sessionId, itemId: lambdaItem.id });

      return {
        id: lambdaItem.id,
        session_id: lambdaItem.session_id,
        source_type: lambdaItem.source_type,
        source_id: lambdaItem.source_id,
        quantity: lambdaItem.quantity,
        original_amount: lambdaItem.original_amount,
        currency: lambdaItem.currency,
        discounted_amount: lambdaItem.discounted_amount,
        addon_cost: lambdaItem.addon_cost,
        selected_addon_services: lambdaItem.selected_addon_services,
        applied_discounts: lambdaItem.applied_discounts,
        selected_options: lambdaItem.selected_options,
        catalog_variant_id: lambdaItem.catalog_variant_id,
        created_at: lambdaItem.created_at,
        updated_at: lambdaItem.updated_at
      };
    } catch (error) {
      logger.error('[ModernCartService] Failed to add cart item', { sessionId, error: error.message });
      throw error;
    }
  }

  /**
   * Remove cart item via Lambda API
   */
  async removeCartItem(itemId: string): Promise<void> {
    try {
      await lambdaCartApiService.removeCartItem(itemId);
      logger.info('[ModernCartService] Removed cart item via Lambda API', { itemId });
    } catch (error) {
      logger.error('[ModernCartService] Failed to remove cart item', { itemId, error: error.message });
      throw error;
    }
  }

  /**
   * Update cart item quantity via Lambda API
   */
  async updateCartItemQuantity(itemId: string, quantity: number): Promise<void> {
    try {
      await lambdaCartApiService.updateCartItemQuantity(itemId, quantity);
      logger.info('[ModernCartService] Updated cart item quantity via Lambda API', { itemId, quantity });
    } catch (error) {
      logger.error('[ModernCartService] Failed to update cart item quantity', { itemId, quantity, error: error.message });
      throw error;
    }
  }

  /**
   * Clear all items from cart session via Lambda API
   */
  async clearCartItems(sessionId: string): Promise<void> {
    try {
      await lambdaCartApiService.clearCartItems(sessionId);
      logger.info('[ModernCartService] Cleared cart items via Lambda API', { sessionId });
    } catch (error) {
      logger.error('[ModernCartService] Failed to clear cart items', { sessionId, error: error.message });
      throw error;
    }
  }
}

// Export singleton instance
export const modernCartService = ModernCartService.getInstance();